import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  Block as BlockedIcon,
  SkipNext as SkippedIcon,
  Speed as MetricIcon,
} from '@mui/icons-material';
import { SqlDataPayload } from 'types';

// ─── Status color mapping ────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  passed: '#4caf50',
  pass: '#4caf50',
  success: '#4caf50',
  completed: '#4caf50',
  active: '#4caf50',
  failed: '#f44336',
  fail: '#f44336',
  error: '#f44336',
  blocked: '#ff9800',
  warning: '#ff9800',
  skipped: '#9e9e9e',
  skip: '#9e9e9e',
  pending: '#2196f3',
  running: '#2196f3',
  in_progress: '#2196f3',
  draft: '#78909c',
  deprecated: '#78909c',
  not_run: '#bdbdbd',
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status.toLowerCase().replace(/\s+/g, '_')] || '#78909c';
}

// ─── MetricCards ─────────────────────────────────────────────────────────────

interface MetricCardsProps {
  data: SqlDataPayload;
}

function MetricCards({ data }: MetricCardsProps) {
  const cards: { label: string; value: string; color: string }[] = [];

  // Total items
  cards.push({
    label: 'Total Items',
    value: String(data.total_rows),
    color: '#1976d2',
  });

  // Status-based metrics
  const breakdown = data.status_breakdown || {};
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0) || data.total_rows;

  const passedCount = (breakdown['passed'] || 0) + (breakdown['pass'] || 0) + (breakdown['success'] || 0) + (breakdown['completed'] || 0);
  const failedCount = (breakdown['failed'] || 0) + (breakdown['fail'] || 0) + (breakdown['error'] || 0);

  if (Object.keys(breakdown).length > 0) {
    // Pass rate
    if (total > 0) {
      const passRate = ((passedCount / total) * 100).toFixed(1);
      cards.push({
        label: 'Pass Rate',
        value: `${passRate}%`,
        color: parseFloat(passRate) >= 80 ? '#4caf50' : parseFloat(passRate) >= 50 ? '#ff9800' : '#f44336',
      });
    }

    if (passedCount > 0) {
      cards.push({ label: 'Passed', value: String(passedCount), color: '#4caf50' });
    }
    if (failedCount > 0) {
      cards.push({ label: 'Failed', value: String(failedCount), color: '#f44336' });
    }
  }

  // Numeric metrics (show pass_rate_pct if available)
  const metrics = data.metrics || {};
  if (metrics['pass_rate_pct'] && cards.length < 4) {
    cards.push({
      label: 'Avg Pass Rate',
      value: `${metrics['pass_rate_pct'].avg}%`,
      color: metrics['pass_rate_pct'].avg >= 80 ? '#4caf50' : '#ff9800',
    });
  }

  // Cap at 4 cards
  const displayCards = cards.slice(0, 4);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
      {displayCards.map((card, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            flex: '1 1 120px',
            minWidth: 120,
            maxWidth: 180,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {card.label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: card.color }}>
            {card.value}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

// ─── StatusBar ───────────────────────────────────────────────────────────────

interface StatusBarProps {
  breakdown: Record<string, number>;
}

function StatusBar({ breakdown }: StatusBarProps) {
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const segments = Object.entries(breakdown)
    .sort(([, a], [, b]) => b - a)
    .map(([status, count]) => ({
      status,
      count,
      pct: (count / total) * 100,
      color: getStatusColor(status),
    }));

  return (
    <Box sx={{ mb: 2 }}>
      {/* Stacked bar */}
      <Box
        sx={{
          display: 'flex',
          height: 12,
          borderRadius: 1.5,
          overflow: 'hidden',
          mb: 1,
        }}
      >
        {segments.map((seg, i) => (
          <Tooltip key={i} title={`${seg.status}: ${seg.count} (${seg.pct.toFixed(1)}%)`}>
            <Box
              sx={{
                width: `${seg.pct}%`,
                bgcolor: seg.color,
                minWidth: seg.pct > 0 ? 4 : 0,
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>
        ))}
      </Box>
      {/* Legend chips */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {segments.map((seg, i) => (
          <Chip
            key={i}
            size="small"
            label={`${seg.status} (${seg.count})`}
            sx={{
              bgcolor: seg.color + '1A',
              color: seg.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── DataTable ───────────────────────────────────────────────────────────────

type SortOrder = 'asc' | 'desc';

interface DataTableProps {
  data: SqlDataPayload;
}

function DataTable({ data }: DataTableProps) {
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<SortOrder>('asc');

  // Pick display columns (max 6)
  const displayColumns = useMemo(() => {
    const cols = data.columns;
    if (cols.length <= 6) return cols;

    // Prioritize: name, status, key fields, then first remaining
    const priority = [
      data.name_field,
      data.id_field,
      data.status_field,
    ].filter(Boolean) as string[];

    const remaining = cols.filter((c) => !priority.includes(c));
    return [...priority, ...remaining].slice(0, 6);
  }, [data]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return data.rows;
    return [...data.rows].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return order === 'asc' ? cmp : -cmp;
    });
  }, [data.rows, orderBy, order]);

  const handleSort = (col: string) => {
    if (orderBy === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  const isStatusCol = (col: string) =>
    col === data.status_field ||
    col.toLowerCase().includes('status');

  const formatHeader = (col: string) =>
    col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const renderCell = (row: Record<string, any>, col: string) => {
    const val = row[col];
    if (val == null) return <Typography variant="body2" color="text.disabled">—</Typography>;

    if (isStatusCol(col)) {
      const strVal = String(val);
      return (
        <Chip
          size="small"
          label={strVal}
          sx={{
            bgcolor: getStatusColor(strVal) + '1A',
            color: getStatusColor(strVal),
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
          }}
        />
      );
    }

    const strVal = String(val);
    if (strVal.length > 50) {
      return (
        <Tooltip title={strVal}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
            {strVal}
          </Typography>
        </Tooltip>
      );
    }

    return <Typography variant="body2">{strVal}</Typography>;
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxHeight: 400,
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {displayColumns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    bgcolor: 'grey.50',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : 'asc'}
                    onClick={() => handleSort(col)}
                  >
                    {formatHeader(col)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                {displayColumns.map((col) => (
                  <TableCell key={col} sx={{ py: 0.75 }}>
                    {renderCell(row, col)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data.total_rows > data.rows.length && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Showing {data.rows.length} of {data.total_rows} rows
        </Typography>
      )}
    </Box>
  );
}

// ─── Main Visualizer ─────────────────────────────────────────────────────────

interface SqlDataVisualizerProps {
  data: SqlDataPayload;
}

export default function SqlDataVisualizer({ data }: SqlDataVisualizerProps) {
  if (!data || !data.rows || data.rows.length === 0) return null;

  const hasStatusBreakdown = data.status_breakdown && Object.keys(data.status_breakdown).length > 0;

  return (
    <Box sx={{ mb: 2 }}>
      <MetricCards data={data} />
      {hasStatusBreakdown && <StatusBar breakdown={data.status_breakdown} />}
      <DataTable data={data} />
    </Box>
  );
}
